// apps/flux-ev-web/src/components/ev/VehicleView.js
/**
 * VehicleView - Vehicle-focused visualization tab with animated playback
 *
 * Shows time controls (play/pause, speed, scrubber), summary chips,
 * and a table of vehicle states with interpolated SoC that updates
 * as the animation plays.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    alpha,
    LinearProgress,
} from '@mui/material';
import TimeControls from './depot/TimeControls';
import { vortxColors, useThemeMode } from '@vortx/shared-ui';

const STEPS_PER_HOUR = 12;
const TOTAL_STEPS = 24 * STEPS_PER_HOUR; // 288

/**
 * Parse time value to hour number.
 * Handles: number (pass-through), "HH:MM", ISO datetime "2024-06-15T18:30:00"
 */
function parseHour(timeStr) {
    if (typeof timeStr === 'number') return timeStr;
    if (!timeStr) return 0;
    const s = String(timeStr);
    if (s.includes('T')) {
        const timePart = s.split('T')[1];
        return parseInt(timePart.split(':')[0], 10) || 0;
    }
    const parts = s.split(':');
    return parseInt(parts[0], 10) || 0;
}

/**
 * Compute a vehicle's state at a given fractional hour (from 5-min resolution).
 */
function getVehicleStateAtTime(session, fractionalHour) {
    const arrival = session.arrival_hour ?? parseHour(session.return_time);
    const departure = session.departure_hour ?? parseHour(session.departure_time);
    const batteryKwh = session.battery_kwh || 0;
    const initialSoc = session.initial_soc ?? 0;
    const finalSoc = session.final_soc ?? 0;
    const targetSoc = session.target_soc ?? 95;
    const avgRate = session.avg_charging_rate_kw ?? 0;
    const totalEnergy = session.energy_delivered_kwh ?? 0;

    const isOvernight = departure <= arrival;
    const isPresent = isOvernight
        ? (fractionalHour >= arrival || fractionalHour < departure)
        : (fractionalHour >= arrival && fractionalHour < departure);

    if (!isPresent) {
        return {
            status: fractionalHour < arrival ? 'not_arrived' : 'departed',
            soc: null,
            energySoFar: null,
        };
    }

    if (batteryKwh <= 0) {
        return { status: 'charging', soc: initialSoc, energySoFar: 0 };
    }

    const hoursCharging = isOvernight
        ? (fractionalHour >= arrival ? fractionalHour - arrival : 24 - arrival + fractionalHour)
        : fractionalHour - arrival;

    const energyAtTime = Math.min(
        avgRate * Math.max(0, hoursCharging),
        totalEnergy,
    );
    const soc = Math.min(
        initialSoc + (energyAtTime / batteryKwh * 100),
        finalSoc,
    );
    const isComplete = soc >= targetSoc - 1;

    return {
        status: isComplete ? 'complete' : 'charging',
        soc: Math.round(soc),
        energySoFar: Math.round(energyAtTime * 10) / 10,
    };
}

const STATUS_CONFIG = {
    charging: { label: 'Charging', color: vortxColors.teal, variant: 'filled' },
    complete: { label: 'Complete', color: '#4caf50', variant: 'filled' },
    not_arrived: { label: 'Not arrived', color: vortxColors.stone, variant: 'outlined' },
    departed: { label: 'Departed', color: vortxColors.stone, variant: 'filled' },
};

function VehicleView({ metrics, fleetSize, vehicleSessions, paramsUsed }) {
    const { isRetro } = useThemeMode();
    const arrivalHour = paramsUsed?.arrival_hour ?? 18;

    // --- Animation state ---
    const [currentTimeIndex, setCurrentTimeIndex] = useState(arrivalHour * STEPS_PER_HOUR);
    const [isPlaying, setIsPlaying] = useState(true);
    const [speed, setSpeed] = useState(1);
    const animationRef = useRef(null);
    const lastTickRef = useRef(0);

    const animate = useCallback(
        (timestamp) => {
            if (!lastTickRef.current) lastTickRef.current = timestamp;
            const elapsed = timestamp - lastTickRef.current;
            const intervalMs = 24000 / TOTAL_STEPS / speed;

            if (elapsed >= intervalMs) {
                setCurrentTimeIndex((prev) => (prev + 1) % TOTAL_STEPS);
                lastTickRef.current = timestamp;
            }
            animationRef.current = requestAnimationFrame(animate);
        },
        [speed]
    );

    useEffect(() => {
        if (isPlaying) {
            lastTickRef.current = 0;
            animationRef.current = requestAnimationFrame(animate);
        }
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isPlaying, animate]);

    const handleTimeChange = useCallback((index) => {
        setCurrentTimeIndex(index);
        lastTickRef.current = 0;
    }, []);

    const handlePlayPause = useCallback(() => {
        setIsPlaying((prev) => !prev);
    }, []);

    // Current fractional hour from 5-min index
    const fractionalHour = currentTimeIndex / STEPS_PER_HOUR;

    const sessions = vehicleSessions || metrics?.vehicle_sessions || [];

    // Compute vehicle states at the current time
    const vehicleStates = useMemo(() => {
        return sessions.map((session) => ({
            ...session,
            ...getVehicleStateAtTime(session, fractionalHour),
        }));
    }, [sessions, fractionalHour]);

    // Summary counts
    const summary = useMemo(() => {
        let charging = 0, complete = 0, waiting = 0, departed = 0;
        for (const v of vehicleStates) {
            if (v.status === 'charging') charging++;
            else if (v.status === 'complete') complete++;
            else if (v.status === 'not_arrived') waiting++;
            else if (v.status === 'departed') departed++;
        }
        return { charging, complete, waiting, departed };
    }, [vehicleStates]);

    // For large fleets (>50 vehicles), group by fleet group
    const showGrouped = sessions.length > 50;
    const groupedData = useMemo(() => {
        if (!showGrouped) return null;

        const groups = {};
        for (const v of vehicleStates) {
            const match = v.vehicle_id?.match(/^(group_\d+)/);
            const groupId = match ? match[1] : 'unknown';
            if (!groups[groupId]) {
                groups[groupId] = { vehicles: [], charging: 0, complete: 0, waiting: 0, departed: 0 };
            }
            groups[groupId].vehicles.push(v);
            if (v.status === 'charging') groups[groupId].charging++;
            else if (v.status === 'complete') groups[groupId].complete++;
            else if (v.status === 'not_arrived') groups[groupId].waiting++;
            else if (v.status === 'departed') groups[groupId].departed++;
        }
        return Object.entries(groups).map(([id, data]) => {
            const avgSoc = data.vehicles
                .filter(v => v.soc != null)
                .reduce((sum, v, _, arr) => sum + v.soc / arr.length, 0);
            return { groupId: id, ...data, avgSoc: Math.round(avgSoc) };
        });
    }, [showGrouped, vehicleStates]);

    if (sessions.length === 0) {
        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No vehicle session data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Time Controls */}
            <TimeControls
                currentTimeIndex={currentTimeIndex}
                totalSteps={TOTAL_STEPS}
                isPlaying={isPlaying}
                speed={speed}
                onTimeChange={handleTimeChange}
                onPlayPause={handlePlayPause}
                onSpeedChange={setSpeed}
            />

            {/* Summary Chips */}
            <Box sx={{ display: 'flex', gap: 1, my: 2, flexWrap: 'wrap' }}>
                {summary.charging > 0 && (
                    <Chip
                        label={`${summary.charging} Charging`}
                        size="small"
                        sx={{ bgcolor: alpha(vortxColors.teal, 0.15), color: vortxColors.teal, fontWeight: 600 }}
                    />
                )}
                {summary.complete > 0 && (
                    <Chip
                        label={`${summary.complete} Complete`}
                        size="small"
                        sx={{ bgcolor: alpha('#4caf50', 0.15), color: '#2e7d32', fontWeight: 600 }}
                    />
                )}
                {summary.waiting > 0 && (
                    <Chip
                        label={`${summary.waiting} Not arrived`}
                        size="small"
                        variant="outlined"
                        sx={{ color: vortxColors.stone }}
                    />
                )}
                {summary.departed > 0 && (
                    <Chip
                        label={`${summary.departed} Departed`}
                        size="small"
                        sx={{ bgcolor: alpha(vortxColors.stone, 0.15), color: vortxColors.stone }}
                    />
                )}
            </Box>

            {/* Vehicle Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    bgcolor: alpha(vortxColors.teal, 0.03),
                    border: `1px solid ${alpha(vortxColors.teal, 0.1)}`,
                    borderRadius: 1,
                    maxHeight: 420,
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                                {showGrouped ? 'Group' : 'Vehicle'}
                            </TableCell>
                            {!showGrouped && (
                                <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>Charger</TableCell>
                            )}
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, minWidth: 120 }}>SoC</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Target</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Energy</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {showGrouped ? (
                            groupedData.map((group) => (
                                <TableRow key={group.groupId}>
                                    <TableCell sx={{ fontSize: 12 }}>
                                        {group.groupId} ({group.vehicles.length} vehicles)
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12 }}>
                                        {group.avgSoc != null && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(group.avgSoc, 100)}
                                                    sx={{
                                                        flex: 1,
                                                        height: 6,
                                                        borderRadius: 3,
                                                        bgcolor: alpha(vortxColors.teal, 0.1),
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: group.avgSoc >= 90 ? '#4caf50' : vortxColors.teal,
                                                            borderRadius: 3,
                                                            transition: 'transform 0.3s ease',
                                                        },
                                                    }}
                                                />
                                                <Typography variant="caption" sx={{ minWidth: 32 }}>
                                                    {group.avgSoc}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: 12 }}>-</TableCell>
                                    <TableCell align="right" sx={{ fontSize: 12 }}>-</TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12 }}>
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            {group.charging > 0 && (
                                                <Chip label={group.charging} size="small"
                                                    sx={{ bgcolor: alpha(vortxColors.teal, 0.15), color: vortxColors.teal, height: 20, fontSize: 11 }} />
                                            )}
                                            {group.complete > 0 && (
                                                <Chip label={group.complete} size="small"
                                                    sx={{ bgcolor: alpha('#4caf50', 0.15), color: '#2e7d32', height: 20, fontSize: 11 }} />
                                            )}
                                            {group.waiting > 0 && (
                                                <Chip label={group.waiting} size="small" variant="outlined"
                                                    sx={{ color: vortxColors.stone, height: 20, fontSize: 11 }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            vehicleStates.map((v) => {
                                const statusConf = STATUS_CONFIG[v.status] || STATUS_CONFIG.not_arrived;
                                return (
                                    <TableRow
                                        key={v.vehicle_id}
                                        sx={{
                                            transition: 'background-color 0.3s ease',
                                            bgcolor: v.status === 'charging'
                                                ? alpha(vortxColors.teal, isRetro ? 0.08 : 0.04)
                                                : 'transparent',
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: 12 }}>
                                            {v.vehicle_id}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12 }}>
                                            {v.charger_id != null ? `#${v.charger_id + 1}` : '-'}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12 }}>
                                            {v.soc != null ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(v.soc, 100)}
                                                        sx={{
                                                            flex: 1,
                                                            height: 6,
                                                            borderRadius: 3,
                                                            bgcolor: alpha(vortxColors.teal, 0.1),
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: v.soc >= (v.target_soc - 1) ? '#4caf50' : vortxColors.teal,
                                                                borderRadius: 3,
                                                                transition: 'transform 0.3s ease',
                                                            },
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ minWidth: 32 }}>
                                                        {v.soc}%
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                            {v.target_soc}%
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                            {v.energySoFar != null ? `${v.energySoFar} kWh` : '-'}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12 }}>
                                            <Chip
                                                label={statusConf.label}
                                                size="small"
                                                variant={statusConf.variant}
                                                sx={{
                                                    height: 20,
                                                    fontSize: 11,
                                                    ...(statusConf.variant === 'filled'
                                                        ? { bgcolor: alpha(statusConf.color, 0.15), color: statusConf.color }
                                                        : { borderColor: statusConf.color, color: statusConf.color }
                                                    ),
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default VehicleView;
